// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// path.ts — pointer samples to a route. PURE, and written before a single pixel existed.
//
// This is the whole game's input, so it is a function of (samples, geometry, start) and nothing else:
// no DOM, no time, no state. That is what lets tests/path.test.ts drive the real gesture at both row
// pitches, at realistic sample spacing, with no browser — and it is why the browser pass only has to
// prove the WIRING, not the algorithm. (Automation cannot draw an L: `left_click_drag` is two points
// on a straight line and a straight line never enters the corner cell of an L-shaped route.)
//
// Four rules earn their place, each because of a specific way a thumb behaves:
//
//  1. BRESENHAM-ISH INTERPOLATION between consecutive samples. A pointermove stream on a fast sweep
//     is ~16ms apart, which at flick speed is 60-100px — more than a cell. Without interpolation the
//     route silently skips the cell you dragged through, and the player's line "did less than they
//     drew". Never truncate at the first non-adjacent sample; walk the line.
//  2. DOMINANT-AXIS LOCK. A thumb dragging up a column rolls 5-15px sideways on the way. Requiring
//     60% of a cell width of lateral displacement before a sideways step is accepted makes that roll
//     a no-op, while a deliberate sideways move (a full pitch) passes easily.
//  3. TRUNCATE-ON-REVISIT. Re-entering a cell already on the path cuts back to it. When the revisited
//     cell is the one before the end this is exactly "backtrack pops one segment" — the same rule,
//     not a special case, which is why it cannot pop two.
//  4. A DETERMINISTIC CORNER TIE-BREAK. When the interpolated line crosses a corner, prefer the axis
//     with the larger remaining displacement; on an exact tie, change ROW first. Documented and
//     tested rather than left to whichever way the arithmetic happened to fall, because the live
//     under-finger preview means the player can see and correct a deterministic choice — but cannot
//     correct one that varies.

export interface Geom {
  cols: number;
  rows: number;
  /** Compartment box, excluding the gap. */
  cellW: number;
  cellH: number;
  /** Gutter between compartments. */
  gap: number;
}

export interface Sample {
  x: number;
  y: number;
}

/** Lateral displacement, as a fraction of a cell's own width/height, before a step is accepted. */
export const AXIS_LOCK = 0.6;

/** A route can never be longer than the board; a runaway would be a bug, not a gesture. */
const MAX_STEPS = 64;

export const cellX = (g: Geom, i: number): number => i % g.cols;
export const cellY = (g: Geom, i: number): number => (i / g.cols) | 0;
export const cellIndex = (g: Geom, x: number, y: number): number => y * g.cols + x;

const pitchX = (g: Geom): number => g.cellW + g.gap;
const pitchY = (g: Geom): number => g.cellH + g.gap;

/** Centre of a compartment in the grid's own coordinate space. */
export function centreOf(g: Geom, i: number): Sample {
  return {
    x: cellX(g, i) * pitchX(g) + g.cellW / 2,
    y: cellY(g, i) * pitchY(g) + g.cellH / 2,
  };
}

/** Which compartment a point is over, or -1 if it is off the board. */
export function cellAt(g: Geom, x: number, y: number): number {
  if (x < 0 || y < 0) return -1;
  const cx = Math.floor(x / pitchX(g));
  const cy = Math.floor(y / pitchY(g));
  if (cx < 0 || cx >= g.cols || cy < 0 || cy >= g.rows) return -1;
  return cellIndex(g, cx, cy);
}

/**
 * The orthogonal chain from `from` (exclusive) to `to` (inclusive).
 *
 * Rule 4 lives here. Each step goes along whichever axis has more ground left to cover; an exact tie
 * changes row first. Both endpoints are real cells, so the chain is always finite and orthogonal.
 */
export function chain(g: Geom, from: number, to: number): number[] {
  const out: number[] = [];
  let x = cellX(g, from);
  let y = cellY(g, from);
  const tx = cellX(g, to);
  const ty = cellY(g, to);
  let guard = 0;
  while ((x !== tx || y !== ty) && guard++ < MAX_STEPS) {
    const dx = Math.abs(tx - x);
    const dy = Math.abs(ty - y);
    if (dy >= dx && dy > 0) y += ty > y ? 1 : -1;
    else x += tx > x ? 1 : -1;
    out.push(cellIndex(g, x, y));
  }
  return out;
}

/**
 * Turn a stream of pointer samples into a route.
 *
 * `samples` are already relative to the grid's content box (clientX/Y minus getBoundingClientRect —
 * never offsetX/offsetY, which scales oddly under DPR and zoom). The result always begins at
 * `startCell`, is orthogonal, self-avoiding, and never longer than the board.
 *
 * `allow` is an optional per-cell filter — the tutorial uses it to refuse a route through a cell a
 * step has not unlocked yet. A rejected cell simply ends the walk for that sample; it is never fatal.
 */
export function pathFromPointerSamples(
  samples: readonly Sample[],
  g: Geom,
  startCell: number,
  allow?: (cell: number) => boolean,
): number[] {
  const path = [startCell];
  const seen = new Map<number, number>([[startCell, 0]]);
  let cur = startCell;

  for (const s of samples) {
    if (!Number.isFinite(s.x) || !Number.isFinite(s.y)) continue;
    const raw = cellAt(g, s.x, s.y);
    if (raw < 0 || raw === cur) continue; // off the board, or still in the same compartment

    // Rule 2 — the axis lock, measured from the CENTRE of the last committed compartment.
    const c = centreOf(g, cur);
    const freeX = Math.abs(s.x - c.x) > AXIS_LOCK * g.cellW;
    const freeY = Math.abs(s.y - c.y) > AXIS_LOCK * g.cellH;
    const tx = freeX ? cellX(g, raw) : cellX(g, cur);
    const ty = freeY ? cellY(g, raw) : cellY(g, cur);
    const target = cellIndex(g, tx, ty);
    if (target === cur) continue; // a thumb roll, not a step

    // Rule 1 — walk every compartment on the way, not just the one the sample landed in.
    for (const next of chain(g, cur, target)) {
      if (allow && !allow(next)) break;
      const at = seen.get(next);
      if (at !== undefined) {
        // Rule 3 — revisiting truncates back to that compartment. When `next` is the one before the
        // end this pops exactly one segment, which is why backtracking can never pop two.
        for (let k = at + 1; k < path.length; k++) seen.delete(path[k]);
        path.length = at + 1;
      } else {
        if (path.length >= MAX_STEPS) break;
        seen.set(next, path.length);
        path.push(next);
      }
      cur = next;
    }
  }
  return path;
}
